import { Text } from '@mantine/core';
import { neutralColors } from '@styles/design-system';
import { type FC } from 'react';

import styles from './OpenOrdersBanner.module.css';
import type { OrderTotals } from './orderTotals';
import { formatCount } from '@utils/currency';

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/**
 * The site's workload, said in one line.
 *
 * ABOVE THE NUDGES ON PURPOSE. What is outstanding is the reason somebody opened the portal;
 * batches waiting on QC matter, but they are an exception within the job rather than the job.
 * Putting the exception first made the page read as a list of problems.
 *
 * OPEN IS ORDERED MINUS SENT, measured at the gate rather than the line. Goods declared and
 * still on the floor are the site's problem, so counting them as done would report the job
 * finished while the brand has received nothing.
 *
 * DELIBERATELY NOT A NUDGE, though it now borrows the strip's amber. The strip is
 * exceptions-only and renders nothing when there is nothing wrong; an item that is always
 * present would make it permanent furniture. Sharing the colour is safe here because the stock
 * nudges have moved to the Stock screen, so nothing on this page competes with it.
 */
interface OpenOrdersBannerProps {
    totals: OrderTotals;
    /**
     * Batches waiting on a QC verdict, shown only where somebody can act on it.
     *
     * THE BRAND'S VIEW CARRIES THIS AND THE SITE'S DOES NOT, deliberately. Finished-goods
     * quality is the brand's signature — a site shown a count it is not allowed to clear would
     * be shown a job it cannot do, which is how a figure gets learned as noise.
     */
    pendingQc?: number;
    /** Makes the QC figure the way in to the list it counts. */
    onPendingQcClick?: () => void;
}

const OpenOrdersBanner: FC<OpenOrdersBannerProps> = ({
    totals,
    pendingQc,
    onPendingQcClick,
}) => {
    // Nothing open AND nothing waiting is a quiet site, not an empty banner.
    if (totals.orderCount === 0 && !pendingQc) return null;

    return (
        <div className={styles.banner}>
            <Text className={styles.headline} c={neutralColors.textPrimary}>
                {totals.orderCount} production{' '}
                {plural(totals.orderCount, 'order', 'orders')} open
            </Text>
            <Text className={styles.detail} c={neutralColors.textSecondary}>
                {totals.skuCount} {plural(totals.skuCount, 'SKU', 'SKUs')} ·{' '}
                {formatCount(totals.openUnits)}{' '}
                {plural(totals.openUnits, 'unit', 'units')} still to send
            </Text>
            {pendingQc ? (
                <Text
                    className={`${styles.detail} ${onPendingQcClick ? styles.action : ''}`}
                    c={neutralColors.textSecondary}
                    onClick={onPendingQcClick}
                    onKeyDown={(event) => {
                        if (!onPendingQcClick) return;
                        if (event.key === 'Enter' || event.key === ' ') onPendingQcClick();
                    }}
                    role={onPendingQcClick ? 'button' : undefined}
                    tabIndex={onPendingQcClick ? 0 : undefined}
                >
                    · {pendingQc} {plural(pendingQc, 'batch', 'batches')} awaiting your QC
                </Text>
            ) : null}
        </div>
    );
};

export default OpenOrdersBanner;
